import { RefCallback, memo, useCallback, useEffect } from 'react'

import {
  TrExpandable,
  Td,
  useCheckStateContext,
  fancyRowExpandedHeaderClassName,
  useFancyRowExpandedStyle,
  FancyRowHeader,
  TrExpandedContent,
} from 'app/common/ui-components/table'


import { Column } from './columns'
import { Cell } from './cell'
import { getStatusColor } from './statusColors'
import { SubTableInfo } from './subTableInfo'
import SimpleDate from 'app/common/classes/simple-date'
import { EncashmentsDetailsType, EncashmentsStatusType, EncashmentsSummaryType } from 'app/dataTypes/reports/data'
import { EncashmentsAction, EncashmentsStatusAction } from 'app/dataTypes/reports/data/finances/encashments/action'


type Props = {
  ID: string
  columns: Array<Column>
  disabled?: boolean
  columnProps: Record<Column, {
    alignLeft?: boolean
    alignRight?: boolean
    alignCenter?: boolean
  }>
  encashmentsSummary: EncashmentsSummaryType
  encashmentsDetails: Array<EncashmentsDetailsType>
  date: SimpleDate
  index: number
  calcTopShift: (index: number) => number
  trElem: HTMLTableRowElement | null
  updateRowNode: (el: HTMLTableRowElement | null, index: number) => void
  onChangeStatus: (params: EncashmentsStatusAction) => Promise<boolean>
  innerTableHeaderRef: RefCallback<'tr'>
  selectionMode: null | EncashmentsAction
  selection: Set<string>
  onChangeSelection: (orgID: string, checked: boolean) => void
  onRowDataUpdated: () => void
}

export const TableRow = memo(({
  ID,
  columns,
  disabled,
  columnProps,
  encashmentsSummary,
  encashmentsDetails,
  date,
  index,
  calcTopShift,
  trElem,
  updateRowNode,
  onChangeStatus,
  innerTableHeaderRef,
  selectionMode,
  selection,
  onChangeSelection,
  onRowDataUpdated
}: Props) => {
  const [expandedID, setExpandedID] = useCheckStateContext()
  const expanded = expandedID === ID
  const tinted = Boolean(expandedID && expandedID !== ID)
  const { status } = encashmentsSummary
  const color = getStatusColor(status)
  const fancyStyle = useFancyRowExpandedStyle(color)

  const reference = useCallback((el: HTMLTableRowElement | null) => {
    updateRowNode(el, index)
  }, [index, updateRowNode])

  useEffect(() => {
    if (expanded) {
      onRowDataUpdated()
    }
  }, [expanded]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <TrExpandable
      classNameExpanded={fancyRowExpandedHeaderClassName}
      style={fancyStyle}
      styleExpanded={fancyStyle}
      reference={reference}
      id={ID}
      expandedContent={
        <TrExpandedContent fancy>
          {() => (
            <SubTableInfo
              encashmentsDetails={encashmentsDetails}
              encashmentsSummary={encashmentsSummary}
              headerRef={innerTableHeaderRef}
            />
          )}
        </TrExpandedContent>
      }
      expanded={expanded}
      onExpand={setExpandedID}
      disabled={disabled}
      disableExpand={status === EncashmentsStatusType.CashNotDeposited}
      tinted={tinted}
      hoverHighlighted={!expanded && status !== EncashmentsStatusType.CashNotDeposited}
      columnsAmount={columns.length}
      interlineBorder
      alignTop
    >
      {columns.map(key => (
        <Td key={key} {...columnProps[key]}>
          <Cell
            columnKey={key}
            date={date}
            encashmentsSummary={encashmentsSummary}
            onChangeStatus={onChangeStatus}
            selectionMode={selectionMode}
            selection={selection}
            onChangeSelection={onChangeSelection}
          />
        </Td>
      ))}

      <FancyRowHeader
        expanded={expanded}
        requestTopShift={calcTopShift}
        rowIndex={index}
        color={color}
        trElem={trElem}
      />
    </TrExpandable>
  )
})
